import { memo, useCallback, type PropsWithoutRef } from 'react'

import { AutoDeleteOutlined, Delete } from '@mui/icons-material'
import { Box, IconButton, Tooltip } from '@mui/material'
import { ImageObject } from '@nativewaves/nw-cloud-sdk/content'
import { Row, Table } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'

import { useImageObjectDeleteMutation } from 'hooks/mutations/content'
import { useConfirm } from 'hooks/utils'

type SelectionToolbarActionProps = {
  selectedRows: Row<ImageObject>[]
  tableInstance: Table<ImageObject>
  onLifecyclePolicyClick?: () => void
}

const SelectionToolbarActionsRaw = ({
  selectedRows,
  tableInstance,
  onLifecyclePolicyClick,
}: PropsWithoutRef<SelectionToolbarActionProps>) => {
  const { t } = useTranslation(['common', 'domain'])
  const { mutateAsync: deleteImageObject } = useImageObjectDeleteMutation()
  const confirm = useConfirm()

  const handleDeleteSelectedImageObjects = useCallback(async () => {
    try {
      await confirm(t('areYouSure'))

      await Promise.allSettled(selectedRows.map((row) => deleteImageObject({ imageId: row.original.id })))

      tableInstance.resetRowSelection()
    } catch (_) {}
  }, [confirm, deleteImageObject, selectedRows, t, tableInstance])

  return (
    <Box sx={{ ml: 'auto', mr: 2, display: 'flex', alignItems: 'center', columnGap: 1 }}>
      <Tooltip title={t('domain:Workspace.Image.setLifecyclePolicy', { count: selectedRows.length })} placement="top">
        <IconButton sx={{ ml: 'auto', mr: 0.5, my: 'auto' }} size="medium" onClick={onLifecyclePolicyClick}>
          <AutoDeleteOutlined fontSize="medium" />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('domain:Workspace.Image.deleteImage', { count: selectedRows.length })} placement="top">
        <IconButton sx={{ ml: 'auto', mr: 2, my: 'auto' }} size="medium" onClick={handleDeleteSelectedImageObjects}>
          <Delete fontSize="medium" />
        </IconButton>
      </Tooltip>
    </Box>
  )
}

export const SelectionToolbarActions = memo(SelectionToolbarActionsRaw)
